/* custom-nutrition-compare.js: progressive enhancement for sections/custom-nutrition-compare.liquid [RP-027].
   The section is complete without this file: rows arrive in their starting order and every number is in the HTML. This
   only re-sorts, filters and announces. Quick add is Impact's own <quick-buy-drawer>, so there is no cart code here. */
class CustomNutritionCompare extends HTMLElement {
  // Listeners go on once here: the theme editor reconnects a section when it is moved, which would add them twice
  constructor() {
    super();

    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-sort], [data-show]')) return;
      if (event.target.matches('[data-sort]')) this.reversed = false;
      this.revealChip(event.target);
      this.update();
    });

    // Desktop column headers drive the same radios as the phone chips; clicking the sorted column again reverses it
    this.addEventListener('click', (event) => {
      const header = event.target.closest('button[data-sort-key]');
      const input = header && this.querySelector(`input[data-sort][value="${header.dataset.sortKey}"]`);
      if (!input) return;
      this.reversed = input.checked ? !this.reversed : false;
      input.checked = true;
      this.update();
    });
  }

  connectedCallback() {
    this.tbody = this.querySelector('tbody');
    this.status = this.querySelector('[data-status]');
  }

  // Centre the chosen chip in its scrolling row, as Custom filter links does for the active link
  revealChip(input) {
    const chip = input.closest('label');
    const row = chip?.parentElement;
    if (!row || row.scrollWidth <= row.clientWidth) return;
    const left = chip.getBoundingClientRect().left - row.getBoundingClientRect().left - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollBy({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  update() {
    const sort = this.querySelector('input[data-sort]:checked');
    const show = this.querySelector('input[data-show]:checked')?.value || 'all';
    const reversed = Boolean(this.reversed);
    const rules = sort ? sort.dataset.order.split(' ').map((rule) => rule.split(':')) : [];
    if (reversed && rules.length) rules[0][1] = rules[0][1] === 'desc' ? 'asc' : 'desc';
    const rows = [...this.tbody.rows];

    // Same order as the Liquid: numbers by the rules, then the lowercase name
    rows.sort((a, b) => {
      for (const [key, dir] of rules) {
        const x = a.dataset[key];
        const y = b.dataset[key];
        const diff = key === 'name' ? (x < y ? -1 : x > y ? 1 : 0) : x - y;
        if (diff) return dir === 'desc' ? -diff : diff;
      }
      return 0;
    });

    const apply = () => {
      let shown = 0;
      for (const row of rows) {
        row.hidden = show !== 'all' && !row.dataset.tags.split(' ').includes(show);
        shown += !row.hidden;
        this.tbody.append(row);
      }

      if (sort) {
        this.querySelectorAll('[data-col]').forEach((element) => element.toggleAttribute('data-active', element.dataset.col === sort.value));
        this.querySelectorAll('th[data-col]').forEach((th) => {
          if (th.dataset.col === sort.value) th.setAttribute('aria-sort', rules[0][1] === 'desc' ? 'descending' : 'ascending');
          else th.removeAttribute('aria-sort');
        });
      }

      if (this.status) {
        const text = this.status.dataset.template.replace('[shown]', shown).replace('[total]', rows.length);
        const { highFirst, lowFirst } = this.status.dataset;
        const label = sort && (reversed ? `${sort.dataset.heading}, ${rules[0][1] === 'desc' ? highFirst : lowFirst}` : sort.dataset.label);
        this.status.textContent = label ? `${label}. ${text}` : text;
      }
    };

    // A hidden tab aborts transitions (InvalidStateError), so swap instantly there too
    if (!document.startViewTransition || document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches) return apply();
    document.startViewTransition(apply).ready.catch(() => {});
  }
}

if (!customElements.get('custom-nutrition-compare')) {
  customElements.define('custom-nutrition-compare', CustomNutritionCompare);
}